import { useState, useEffect } from "react";
import { api, useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calculator, ShoppingCart, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";

export default function Calculadora() {
  const { addToCart } = useCart();
  const [materials, setMaterials] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [area, setArea] = useState("");
  const [thickness, setThickness] = useState("");
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await api.get("/calculator/materials");
      setMaterials(response.data);
    } catch (error) {
      console.error("Error fetching materials:", error);
    }
  };

  const handleCalculate = async () => {
    if (!selectedMaterial || !area || parseFloat(area) <= 0) {
      toast.error("Por favor completá todos los campos");
      return;
    }

    setCalculating(true);
    try {
      const response = await api.post("/calculator/calculate", {
        material_type: selectedMaterial,
        area_m2: parseFloat(area),
        thickness_cm: thickness ? parseFloat(thickness) : null,
      });
      setResult(response.data);
    } catch (error) {
      toast.error("Error al calcular");
      console.error("Calculation error:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setSelectedMaterial("");
    setArea("");
    setThickness("");
    setResult(null);
  };

  const materialList = Object.entries(materials).map(([key, value]) => ({
    key,
    ...value,
  }));

  return (
    <div className="min-h-screen bg-background" data-testid="calculadora-page">
      {/* Header */}
      <div className="bg-primary text-white py-8 relative">
        <div className="relative industrial-texture"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl">CALCULADORA DE MATERIALES</h1>
          <p className="text-white/80 mt-2">
            Calculá cuánto material necesitás para tu obra
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-sm border p-6 md:p-8">
          {/* Info box */}
          <div className="bg-muted rounded p-4 mb-6 flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1">¿Cómo funciona?</p>
              <p className="text-muted-foreground">
                Ingresá el área en metros cuadrados y seleccioná el tipo de material.
                La calculadora te dará una estimación de la cantidad necesaria.
                Los cálculos incluyen un margen de desperdicio.
              </p>
            </div>
          </div>

          {/* Calculator form */}
          <div className="space-y-6">
            <div>
              <Label htmlFor="material" className="text-base font-heading uppercase tracking-wide">
                Tipo de Material
              </Label>
              <Select value={selectedMaterial} onValueChange={setSelectedMaterial}>
                <SelectTrigger className="h-12 mt-2" data-testid="material-select">
                  <SelectValue placeholder="Seleccioná un material" />
                </SelectTrigger>
                <SelectContent>
                  {materialList.map((mat) => (
                    <SelectItem key={mat.key} value={mat.key}>
                      <div className="flex flex-col items-start">
                        <span className="capitalize">{mat.key.replace(/_/g, " ")}</span>
                        <span className="text-xs text-muted-foreground">{mat.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="area" className="text-base font-heading uppercase tracking-wide">
                Área (m²)
              </Label>
              <Input
                id="area"
                type="number"
                min="0"
                step="0.1"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                placeholder="Ej: 50"
                className="h-12 mt-2"
                data-testid="area-input"
              />
            </div>

            {selectedMaterial === "hormigon" && (
              <div>
                <Label htmlFor="thickness" className="text-base font-heading uppercase tracking-wide">
                  Espesor (cm)
                </Label>
                <Input
                  id="thickness"
                  type="number"
                  min="0"
                  step="1"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  placeholder="Ej: 10"
                  className="h-12 mt-2"
                  data-testid="thickness-input"
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleCalculate}
                disabled={calculating}
                className="flex-1 bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide h-12"
                data-testid="calculate-btn"
              >
                <Calculator className="w-5 h-5 mr-2" />
                {calculating ? "Calculando..." : "Calcular"}
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                className="h-12"
              >
                <RefreshCw className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className="mt-8 calculator-result rounded-sm p-6 text-white" data-testid="calculator-result">
              <h3 className="font-heading text-xl mb-4 uppercase tracking-wide">Resultado</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-white/70 text-sm">Material</p>
                  <p className="font-heading text-lg capitalize">{result.material.replace(/_/g, " ")}</p>
                </div>
                <div>
                  <p className="text-white/70 text-sm">Área ingresada</p>
                  <p className="font-heading text-lg">{result.area_m2} m²</p>
                </div>
                {result.waste_percent > 0 && (
                  <>
                    <div>
                      <p className="text-white/70 text-sm">Desperdicio incluido</p>
                      <p className="font-heading text-lg text-accent">+{result.waste_percent}%</p>
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Área total a cubrir</p>
                      <p className="font-heading text-lg">{result.area_with_waste} m²</p>
                    </div>
                  </>
                )}
              </div>

              <div className="bg-white/10 rounded p-4 text-center">
                <p className="text-white/70 text-sm mb-1">Cantidad Necesaria</p>
                <p className="font-heading text-4xl md:text-5xl">
                  {result.quantity} <span className="text-xl md:text-2xl">{result.unit}</span>
                </p>
                <p className="text-white/60 text-sm mt-2">{result.description}</p>
              </div>

              {/* Calculation detail */}
              {result.calculation_detail && (
                <div className="mt-4 bg-white/5 rounded p-3 border border-white/10">
                  <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Cálculo:</p>
                  <p className="text-white/80 text-sm font-mono">{result.calculation_detail}</p>
                </div>
              )}

              {/* Tip */}
              {result.tip && (
                <div className="mt-4 flex items-start gap-2 text-white/70 text-sm">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-accent" />
                  <p>{result.tip}</p>
                </div>
              )}

              <p className="text-white/50 text-xs mt-4 text-center">
                * Esta es una estimación. Los valores reales pueden variar según las condiciones de la obra.
              </p>
            </div>
          )}
        </div>

        {/* Material guide */}
        <div className="mt-8 bg-white rounded-sm border p-6">
          <h2 className="font-heading text-xl mb-4 uppercase tracking-wide">Guía de Materiales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {materialList.map((mat) => (
              <div key={mat.key} className="p-4 bg-muted rounded border border-border/50">
                <h4 className="font-medium capitalize text-primary">{mat.key.replace(/_/g, " ")}</h4>
                <p className="text-sm text-muted-foreground mt-1">{mat.description}</p>
                {mat.waste_percent > 0 && (
                  <p className="text-xs text-accent mt-2 font-medium">
                    +{mat.waste_percent}% desperdicio incluido
                  </p>
                )}
                {mat.tip && (
                  <p className="text-xs text-muted-foreground mt-1 italic">
                    {mat.tip}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
